import React, { FC } from "react"
import L10n from "lib/carrot-l10n"
import { useDispatch, useSelector } from "react-redux"
import { FormProvider, useForm } from "react-hook-form"
import { Spacer } from "../../atoms/Atoms"
import { Body } from "../../atoms/Bodies"
import { updateUserInfo } from "../../../../actions/userActions"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "./BenefitEnrollmentFlowModal"
import { Steps } from "./Workflow/Steps"
import useSubmitWithErrorCatch from "./hooks/useSubmitWithErrorCatch"
import { useProgressTracker } from "../ProgressTracker"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { getMemberHasHsaWithDataFix } from "../../../../derivedSelectors/enrollmentDataFix"
import { H2 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const FieldName = "memberHasHSAValue"

interface MemberHasHSAViewProps {
  name: any
}

const MemberHasHSAIndividualView: FC = () => <HSAView name={Steps.MEMBER_HSA_INDIVIDUAL} />

const MemberHasHSAFamilyView: FC = () => <HSAView name={Steps.MEMBER_HSA_FAMILY} />

const HSAView: FC<MemberHasHSAViewProps> = ({ name }) => {
  const memberHasHSA = useSelector(getMemberHasHsaWithDataFix)
  const { setNextStep } = useStateMachine(name, memberHasHSA)
  const dispatch = useDispatch()

  const { onSubmit } = useSubmitWithErrorCatch(async (args) => {
    const val = args[FieldName].id
    val !== "IM_NOT_SURE" && (await dispatch(updateUserInfo({ memberHasHSA: val })))
    setNextStep(name, val)
  })

  return <HSAForm onSubmit={onSubmit} memberHasHSA={memberHasHSA} />
}

const HSAForm = ({ onSubmit, memberHasHSA }) => {
  const { setProgress } = useProgressTracker()
  setProgress(1, "YOUR INSURANCE COVERAGE")

  const formMethods = useForm()
  const { handleSubmit } = formMethods

  return (
    <FormProvider {...formMethods}>
      <BenefitEnrollmentFlowModalBackButton />

      <Spacer height={3} />
      <H2 variant="dark">{l`Are you currently enrolled in a high deductible health plan (HDHP) with a health savings account (HSA)?`}</H2>
      <Spacer height={0.5} />
      <Body color="black-80">{l`An HDHP has lower monthly premiums and a higher deductible than a traditional plan. An HSA lets you use pre-tax income to pay for eligible medical expenses, and is only available with an HDHP.`}</Body>
      <Spacer height={1} />
      <Body color="black-80">{l`If you’re not sure about your plan details, we’ll connect you with the Care Team to help you figure it out.`}</Body>
      <Spacer height={2} />
      <BenefitEnrollmentModalFormSelect
        name={FieldName}
        defaultValue={memberHasHSA}
        options={[
          { label: l(`Yes`), id: "true" },
          { label: l(`No`), id: "false" },
          { label: l(`I'm not sure`), id: "IM_NOT_SURE" }
        ]}
      />
      <Spacer height={4} />
      <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
    </FormProvider>
  )
}

export { MemberHasHSAIndividualView, MemberHasHSAFamilyView }
